/* ==========================================================================
   vv-cart.js — Vivendo native AJAX cart drawer (replaces UpCart)
   - Renders /cart.js into [data-vv-cart-items] (DOM building, textContent only)
   - Add-on fee product (deschidere colet) shown as a switch, never as a line
   - Qty stepper / remove via /cart/change.js (line keys)
   - Compare-at savings via cached /products/{handle}.js lookups
   - Upsell rail via /recommendations/products.json (first cart item)
   - Opens on any successful /cart/add (fetch + XHR patched) and on header
     cart-icon clicks; updates [data-cart-count] + .t4s-count-box badges
   - window.vvCart = { open, close, refresh }
   ========================================================================== */
(() => {
  'use strict';
  if (window.__vvCartInit) return;
  window.__vvCartInit = true;

  const rawFetch = window.fetch.bind(window);

  const boot = () => {
    const drawer = document.querySelector('[data-vv-cart]');
    const backdrop = document.querySelector('[data-vv-cart-backdrop]');
    if (!drawer || !backdrop) return;

    // Add-on toggles (fee/extra products) — supports multiple switches, each
    // carrying its own data-variant-id. Handles are excluded from upsells.
    const ADDON_HANDLES = new Set(['deschidere-colet-la-livrare', 'produs-surpriza']);
    const moneyFormat = (drawer.dataset.moneyFormat || '{{amount}} lei').replace(/<[^>]*>/g, '');

    const itemsEl = drawer.querySelector('[data-vv-cart-items]');
    const titleCountEl = drawer.querySelector('[data-vv-cart-title-count]');
    const subtotalEl = drawer.querySelector('[data-vv-cart-subtotal]');
    const addonSwitches = Array.from(drawer.querySelectorAll('[data-vv-cart-addon]'));
    const ADDON_IDS = addonSwitches.map((s) => parseInt(s.dataset.variantId, 10)).filter(Boolean);
    const addonsWrap = drawer.querySelector('[data-vv-cart-addons]');
    const upsellsWrap = drawer.querySelector('[data-vv-cart-upsells-wrap]');
    const upsellsEl = drawer.querySelector('[data-vv-cart-upsells]');
    const closeBtn = drawer.querySelector('.vv-cd__close');

    const TRASH_SVG = '<svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 4h11M6.5 4V2.8c0-.4.3-.8.8-.8h1.4c.5 0 .8.4.8.8V4M4 4l.6 9.4c0 .6.5 1.1 1.1 1.1h4.6c.6 0 1.1-.5 1.1-1.1L12 4M6.6 7v5M9.4 7v5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';

    let cart = null;
    let busy = false;
    const addonKeys = new Map(); // addon variant id -> cart line key
    let mesterinoInCart = false;
    let lastFocus = null;
    let renderToken = 0;
    const productCache = new Map(); // handle -> Promise<product|null>
    const recoCache = new Map();    // product_id -> Promise<product[]>

    /* ── Money formatting (same approach as vv-product.js) ────────────── */
    const formatMoney = (cents) => {
      const val = cents / 100;
      const withDelims = (num, decimals, thousands, decimalSep) => {
        const fixed = num.toFixed(decimals);
        const parts = fixed.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.length > 1 ? parts[0] + decimalSep + parts[1] : parts[0];
      };
      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => {
        switch (key) {
          case 'amount': return withDelims(val, 2, ',', '.');
          case 'amount_no_decimals': return withDelims(val, 0, ',', '.');
          case 'amount_with_comma_separator': return withDelims(val, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return withDelims(val, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return withDelims(val, 2, "'", '.');
          default: return m;
        }
      });
    };

    /* ── Helpers ──────────────────────────────────────────────────────── */
    const el = (tag, cls, text) => {
      const node = document.createElement(tag);
      if (cls) node.className = cls;
      if (text !== undefined) node.textContent = text; // safe: text only
      return node;
    };

    const imgSized = (url, w) =>
      url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;

    const jsonFetch = (url, opts) =>
      rawFetch(url, Object.assign({ headers: { Accept: 'application/json' } }, opts || {}))
        .then((r) => {
          if (!r.ok) throw new Error('vv-cart http ' + r.status);
          return r.json();
        });

    const getProduct = (handle) => {
      if (!productCache.has(handle)) {
        productCache.set(handle, jsonFetch('/products/' + handle + '.js').catch(() => null));
      }
      return productCache.get(handle);
    };

    const setBusy = (state) => {
      busy = state;
      if (itemsEl) itemsEl.classList.toggle('is-busy', state);
      addonSwitches.forEach((s) => { s.disabled = state; });
    };

    const updateBadges = () => {
      const n = cart ? cart.item_count : 0;
      document.querySelectorAll('[data-cart-count], .t4s-count-box').forEach((b) => {
        b.textContent = n;
        b.classList.toggle('t4s-op-0', n === 0);
      });
    };

    /* ── Line item row ────────────────────────────────────────────────── */
    const buildRow = (item) => {
      const row = el('div', 'vv-cd-item');
      row.setAttribute('data-vv-cart-line', item.key);

      const thumb = el('a', 'vv-cd-item__thumb');
      thumb.href = item.url || '#';
      if (item.image) {
        const img = document.createElement('img');
        img.src = imgSized(item.image, 160);
        img.alt = item.product_title || '';
        img.width = 76;
        img.height = 76;
        img.loading = 'lazy';
        thumb.appendChild(img);
      }
      row.appendChild(thumb);

      const info = el('div', 'vv-cd-item__info');

      const head = el('div', 'vv-cd-item__head');
      const title = el('a', 'vv-cd-item__title', item.product_title || '');
      title.href = item.url || '#';
      head.appendChild(title);
      const rm = el('button', 'vv-cd-item__rm');
      rm.type = 'button';
      rm.setAttribute('aria-label', 'Șterge');
      rm.innerHTML = TRASH_SVG; // static trusted markup
      rm.addEventListener('click', () => changeLine(item.key, 0));
      head.appendChild(rm);
      info.appendChild(head);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        info.appendChild(el('div', 'vv-cd-item__variant', item.variant_title));
      }

      const foot = el('div', 'vv-cd-item__foot');
      const qty = el('div', 'vv-cd-qty');
      const minus = el('button', 'vv-cd-qty__btn', '−');
      minus.type = 'button';
      minus.setAttribute('aria-label', 'Scade cantitatea');
      minus.addEventListener('click', () => changeLine(item.key, item.quantity - 1));
      const num = el('span', 'vv-cd-qty__num', String(item.quantity));
      const plus = el('button', 'vv-cd-qty__btn', '+');
      plus.type = 'button';
      plus.setAttribute('aria-label', 'Crește cantitatea');
      plus.addEventListener('click', () => changeLine(item.key, item.quantity + 1));
      qty.appendChild(minus);
      qty.appendChild(num);
      qty.appendChild(plus);
      foot.appendChild(qty);

      const priceCol = el('div', 'vv-cd-item__pricecol');
      const prices = el('div', 'vv-cd-item__prices');
      const cmp = el('s', 'vv-cd-item__cmp');
      cmp.hidden = true;
      const price = el('span', 'vv-cd-item__price', formatMoney(item.final_line_price));
      prices.appendChild(cmp);
      prices.appendChild(price);
      const save = el('span', 'vv-cd-item__save');
      save.hidden = true;
      priceCol.appendChild(prices);
      priceCol.appendChild(save);
      foot.appendChild(priceCol);
      info.appendChild(foot);

      row.appendChild(info);
      return row;
    };

    /* ── Compare-at savings enrichment (async, graceful skip) ─────────── */
    const enhanceSavings = (visible, token) => {
      visible.forEach((item) => {
        if (!item.handle) return;
        getProduct(item.handle).then((product) => {
          if (!product || token !== renderToken) return;
          const variant = (product.variants || []).find((v) => v.id === item.variant_id);
          if (!variant || !variant.compare_at_price || variant.compare_at_price <= variant.price) return;
          const row = itemsEl.querySelector('[data-vv-cart-line="' + item.key + '"]');
          if (!row) return;
          const compareTotal = variant.compare_at_price * item.quantity;
          const diff = compareTotal - item.final_line_price;
          if (diff <= 0) return;
          const cmp = row.querySelector('.vv-cd-item__cmp');
          const save = row.querySelector('.vv-cd-item__save');
          if (cmp) { cmp.textContent = formatMoney(compareTotal); cmp.hidden = false; }
          if (save) { save.textContent = 'Economisește ' + formatMoney(diff); save.hidden = false; }
        });
      });
    };

    /* ── Upsell rail ──────────────────────────────────────────────────── */
    const buildUpsellCard = (product) => {
      const card = el('div', 'vv-cd-mini');
      const link = el('a');
      link.href = product.url || '/products/' + product.handle;
      const img = document.createElement('img');
      img.className = 'vv-cd-mini__img';
      const src = product.featured_image || (product.images && product.images[0]) || '';
      if (src) img.src = imgSized(src, 280);
      img.alt = product.title || '';
      img.loading = 'lazy';
      link.appendChild(img);
      card.appendChild(link);

      const title = el('a', 'vv-cd-mini__title', product.title || '');
      title.href = link.href;
      card.appendChild(title);

      const variants = product.variants || [];
      const firstAvailable = variants.find((v) => v.available) || variants[0];
      const priceEl = el('div', 'vv-cd-mini__price', formatMoney((firstAvailable && firstAvailable.price) || product.price));
      card.appendChild(priceEl);

      // Multi-variant recommendations get a compact variant selector; the
      // chosen option drives both the displayed price and what gets added.
      let select = null;
      if (variants.length > 1) {
        select = document.createElement('select');
        select.className = 'vv-cd-mini__select';
        select.setAttribute('aria-label', 'Alege varianta: ' + (product.title || ''));
        variants.forEach((v) => {
          const opt = document.createElement('option');
          opt.value = String(v.id);
          opt.textContent = v.title + ' – ' + formatMoney(v.price);
          if (!v.available) {
            opt.disabled = true;
            opt.textContent = v.title + ' – epuizat';
          }
          select.appendChild(opt);
        });
        if (firstAvailable) select.value = String(firstAvailable.id);
        select.addEventListener('change', () => {
          const v = variants.find((x) => String(x.id) === select.value);
          if (v) priceEl.textContent = formatMoney(v.price);
        });
        card.appendChild(select);
      }

      const btn = el('button', 'vv-cd-mini__add', 'Adaugă');
      btn.type = 'button';
      btn.addEventListener('click', () => {
        let variant = firstAvailable;
        if (select) variant = variants.find((x) => String(x.id) === select.value) || firstAvailable;
        if (variant && variant.available !== false) addVariant(variant.id, btn);
      });
      card.appendChild(btn);
      return card;
    };

    const renderUpsells = (visible, token) => {
      if (!upsellsWrap || !upsellsEl) return;
      if (!visible.length) { upsellsWrap.hidden = true; return; }
      const pid = visible[0].product_id;
      if (!recoCache.has(pid)) {
        recoCache.set(
          pid,
          jsonFetch('/recommendations/products.json?product_id=' + pid + '&limit=6')
            .then((data) => (data && data.products) || [])
            .catch(() => [])
        );
      }
      recoCache.get(pid).then((products) => {
        if (token !== renderToken) return;
        const inCart = new Set((cart.items || []).map((i) => i.product_id));
        const picks = products
          .filter((p) => !inCart.has(p.id) && !ADDON_HANDLES.has(p.handle))
          .filter((p) => (p.variants || []).some((v) => v.available))
          .slice(0, 4);
        upsellsEl.textContent = '';
        if (!picks.length) { upsellsWrap.hidden = true; return; }
        picks.forEach((p) => upsellsEl.appendChild(buildUpsellCard(p)));
        upsellsWrap.hidden = false;
        updateUpsellArrows();
      });
    };

    /* ── Upsell rail arrows (desktop affordance for the hidden scrollbar) ── */
    const upsPrev = drawer.querySelector('[data-vv-cart-ups-prev]');
    const upsNext = drawer.querySelector('[data-vv-cart-ups-next]');
    const updateUpsellArrows = () => {
      if (!upsPrev || !upsNext || !upsellsEl) return;
      const overflow = upsellsEl.scrollWidth - upsellsEl.clientWidth > 2;
      upsPrev.hidden = !overflow || upsellsEl.scrollLeft <= 2;
      upsNext.hidden = !overflow || upsellsEl.scrollLeft + upsellsEl.clientWidth >= upsellsEl.scrollWidth - 2;
    };
    if (upsPrev && upsNext && upsellsEl) {
      upsPrev.addEventListener('click', () => upsellsEl.scrollBy({ left: -upsellsEl.clientWidth * 0.8, behavior: 'smooth' }));
      upsNext.addEventListener('click', () => upsellsEl.scrollBy({ left: upsellsEl.clientWidth * 0.8, behavior: 'smooth' }));
      upsellsEl.addEventListener('scroll', updateUpsellArrows, { passive: true });
      window.addEventListener('resize', updateUpsellArrows);
    }

    /* ── Mesterino gate ───────────────────────────────────────────────── */
    const isMesterinoProduct = (p) => {
      if (!p) return false;
      const hay = ((p.tags || []).join('|') + '|' + (p.vendor || '')).toLowerCase();
      return hay.indexOf('mesterino') !== -1 || hay.indexOf('klugmeister') !== -1;
    };
    const checkMesterino = (visibleLines, token) => {
      if (!addonsWrap) return;
      const handles = Array.from(new Set(visibleLines.map((it) => it.handle)));
      Promise.all(handles.map(getProduct)).then((products) => {
        if (token !== renderToken) return;
        mesterinoInCart = products.some(isMesterinoProduct);
        addonsWrap.hidden = mesterinoInCart;
        // Add-ons must not ship with Mesterino products — silently drop any
        // that were toggled on before the Mesterino item was added.
        if (mesterinoInCart && addonKeys.size && !busy) {
          const firstKey = addonKeys.values().next().value;
          changeLine(firstKey, 0); // refresh loops back; removes one per pass
        }
      });
    };

    /* ── Render ───────────────────────────────────────────────────────── */
    const render = () => {
      if (!cart) return;
      const token = ++renderToken;
      const lines = cart.items || [];
      const visible = lines.filter((it) => ADDON_IDS.indexOf(it.variant_id) === -1);

      addonKeys.clear();
      addonSwitches.forEach((sw) => {
        const id = parseInt(sw.dataset.variantId, 10);
        const line = lines.find((it) => it.variant_id === id);
        if (line) addonKeys.set(id, line.key);
        sw.setAttribute('aria-checked', line ? 'true' : 'false');
        sw.classList.toggle('is-on', !!line);
      });

      // Mesterino gate: fee/extra add-ons are not offered alongside
      // Mesterino products. Checked async from cached /products/{handle}.js.
      checkMesterino(visible, token);

      const count = visible.reduce((sum, it) => sum + it.quantity, 0);
      if (titleCountEl) titleCountEl.textContent = '(' + count + ')';
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      drawer.classList.toggle('is-empty', visible.length === 0);
      updateBadges();

      itemsEl.textContent = '';
      visible.forEach((it) => itemsEl.appendChild(buildRow(it)));
      enhanceSavings(visible, token);
      renderUpsells(visible, token);
    };

    /* ── Cart mutations ───────────────────────────────────────────────── */
    const refresh = () =>
      jsonFetch('/cart.js')
        .then((c) => { cart = c; render(); })
        .catch(() => { /* keep last known state */ });

    const changeLine = (key, quantity) => {
      if (busy) return;
      setBusy(true);
      jsonFetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then((c) => { cart = c; render(); })
        .catch(() => refresh())
        .then(() => setBusy(false));
    };

    const addVariant = (id, btn) => {
      if (busy) return;
      setBusy(true);
      if (btn) btn.disabled = true;
      rawFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: id, quantity: 1 })
      })
        .then((r) => { if (!r.ok) throw new Error('add-failed'); })
        .then(() => refresh())
        .catch(() => refresh())
        .then(() => {
          setBusy(false);
          if (btn) btn.disabled = false;
        });
    };

    /* ── Open / close ─────────────────────────────────────────────────── */
    const open = () => {
      if (drawer.classList.contains('is-open')) return;
      lastFocus = document.activeElement;
      drawer.hidden = false;
      backdrop.hidden = false;
      // Force a style flush so the transition runs from the hidden state.
      // (Deliberately not requestAnimationFrame: rAF never fires in hidden
      // tabs, which would leave the drawer stuck closed after an add-to-cart
      // made while the tab is backgrounded.)
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      backdrop.classList.add('is-open');
      document.body.classList.add('vv-cd-lock');
      if (closeBtn) closeBtn.focus();
      if (!cart) refresh();
    };

    const close = () => {
      if (!drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      document.body.classList.remove('vv-cd-lock');
      window.setTimeout(() => {
        if (!drawer.classList.contains('is-open')) {
          drawer.hidden = true;
          backdrop.hidden = true;
        }
      }, 300);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      lastFocus = null;
    };

    drawer.querySelectorAll('[data-vv-cart-close]').forEach((b) =>
      b.addEventListener('click', close)
    );
    backdrop.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    addonSwitches.forEach((sw) => {
      sw.addEventListener('click', () => {
        if (busy || mesterinoInCart) return;
        const id = parseInt(sw.dataset.variantId, 10);
        const key = addonKeys.get(id);
        if (key) changeLine(key, 0);
        else addVariant(id);
      });
    });

    /* ── Open trigger: header cart icon (intercept /cart navigation) ──── */
    document.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('a[href*="/cart"]');
      if (!a || drawer.contains(a)) return;
      let path;
      try { path = new URL(a.href, window.location.origin).pathname; } catch (err) { return; }
      if (path !== '/cart' && path !== '/cart/') return;
      e.preventDefault();
      refresh();
      open();
    });

    /* ── Open trigger: any successful /cart/add (fetch + XHR) ─────────── */
    let addPingTimer = null;
    const onExternalAdd = () => {
      // Debounce: the fetch hook and the vv:cart:added event may both fire
      // for the same add — coalesce into one refresh+open.
      if (addPingTimer) clearTimeout(addPingTimer);
      addPingTimer = setTimeout(() => { addPingTimer = null; refresh().then(open, open); }, 120);
    };

    // Explicit channel for the theme's own components (vv-product.js ATC,
    // collection quick-add, vv-fbt) — deterministic, immune to fetch races.
    document.addEventListener('vv:cart:added', onExternalAdd);

    // Fetch hook, made tamper-resistant: third-party scripts (analytics,
    // apps) also reassign window.fetch and can silently wipe a plain
    // assignment. A property with a getter/setter keeps our wrapper on top:
    // later assignments become the wrapped inner fetch instead of replacing
    // the hook. A re-entrancy flag prevents loops when another wrapper
    // captured window.fetch (i.e. our wrapper) as its "original".
    let innerFetch = rawFetch;
    let inWrap = false;
    const wrapFetch = function () {
      if (inWrap) return rawFetch.apply(window, arguments);
      const first = arguments[0];
      const url = typeof first === 'string' ? first : (first && first.url) || '';
      inWrap = true;
      let promise;
      try {
        promise = innerFetch.apply(window, arguments);
      } finally {
        inWrap = false;
      }
      if (url.indexOf('/cart/add') !== -1) {
        promise.then((res) => { if (res && res.ok) onExternalAdd(); }, () => {});
      }
      return promise;
    };
    try {
      Object.defineProperty(window, 'fetch', {
        configurable: true,
        get() { return wrapFetch; },
        set(fn) { if (typeof fn === 'function' && fn !== wrapFetch) innerFetch = fn; }
      });
    } catch (e) {
      window.fetch = wrapFetch;
    }

    const xhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__vvCartAdd = typeof url === 'string' && url.indexOf('/cart/add') !== -1;
      return xhrOpen.apply(this, arguments);
    };
    const xhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (this.__vvCartAdd) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) onExternalAdd();
        });
      }
      return xhrSend.apply(this, arguments);
    };

    /* ── Public API + initial state ───────────────────────────────────── */
    window.vvCart = { open: open, close: close, refresh: refresh };
    refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
